import { Action, AuditLogEntityName, OpenPositionType } from "@/api-schema-enums";
import { Can } from "@/casl-provider";
import { useOpenPositionByUuidQuery } from "@/common/api/open-position/use-open-position-by-uuid.query";
import { getCustomerDisplayName } from "@/components/data-table/predefined/customer-table/_helpers/get-customer-display-name";
import { DetailHeader } from "@/components/detail-page/detail-header";
import { DetailPageLayout } from "@/components/detail-page/detail-page-layout";
import { DetailTabs } from "@/components/detail-page/detail-tabs";
import { SystemIdTab } from "@/components/detail-page/tabs/system-id-tab";
import { AuditLogTab } from "@/components/detail-page/tabs/audit-log-tab";
import type { DetailAction, DetailTab } from "@/components/detail-page/types";
import { CenteredLayoutVariant } from "@/components/layouts/centered-layout";
import { Badge, BadgeColorVariant, BadgeVariant } from "@/components/ui/badge";
import { EmptyValue } from "@/components/ui/empty-value";
import { ErrorPage } from "@/components/error-page/error-page";
import { useSyncOpenPosition } from "@/modules/fia/open-position/_hooks/use-sync-open-position";
import { useSyncOpenPositionToV1 } from "@/modules/fia/open-position/_hooks/use-sync-open-position-to-v1";
import { Fingerprint, HistoryIcon, ReceiptEuroIcon, RefreshCwIcon } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useParams } from "wouter";
import { OpenPositionDetailSkeleton } from "./_components/open-position-detail-skeleton";
import { OverviewTab } from "./tabs/overview-tab";

const OPEN_POSITION_DETAIL_TRACKING_NAME = "open-position";

export function OpenPositionDetailView() {
  const params = useParams<{ uuid: string }>();
  const uuid = params.uuid ?? "";

  return (
    <Can I="read" a="OpenPosition">
      <OpenPositionDetailContent uuid={uuid} />
    </Can>
  );
}

function OpenPositionDetailContent({ uuid }: { uuid: string }) {
  const { t } = useTranslation(["fia", "common"]);
  const { data: openPosition, isPending, isError } = useOpenPositionByUuidQuery(uuid);
  const { syncOpenPosition, isSyncing } = useSyncOpenPosition();
  const { syncOpenPositionToV1, isSyncingToV1 } = useSyncOpenPositionToV1();

  if (isPending) {
    return <OpenPositionDetailSkeleton />;
  }

  if (isError || !openPosition) {
    return (
      <ErrorPage
        title={t("fia:OpenPositionNotFound")}
        message={t("fia:OpenPositionDoesNotExist")}
        backTo="~/fia/open-position"
        backToLabel={t("fia:BackToOpenPositions")}
      />
    );
  }

  const isPayment = openPosition.type === OpenPositionType.Payment;

  const detailActions: DetailAction[] = [
    {
      id: "sync",
      label: t("fia:SyncOpenPosition"),
      icon: RefreshCwIcon,
      permission: { action: Action.Read, subject: "OpenPosition" },
      disabled: isSyncing,
      onClick: () => void syncOpenPosition(uuid),
    },
    {
      id: "sync-v1",
      label: t("fia:SyncOpenPositionToV1"),
      icon: RefreshCwIcon,
      permission: { action: Action.Read, subject: "OpenPosition" },
      disabled: isSyncingToV1,
      onClick: () => void syncOpenPositionToV1(uuid),
    },
  ];

  const tabs: DetailTab[] = [
    {
      id: "overview",
      label: t("common:Overview"),
      icon: ReceiptEuroIcon,
      content: () => <OverviewTab openPosition={openPosition} />,
    },
    {
      id: "audit-log",
      label: t("common:AuditLog"),
      icon: HistoryIcon,
      content: () => <AuditLogTab entityName={AuditLogEntityName.OpenPosition} uuid={openPosition.uuid} />,
    },
    {
      id: "identifiers",
      label: t("fia:SystemIds"),
      icon: Fingerprint,
      content: () => (
        <SystemIdTab uuid={openPosition.uuid} createdAt={openPosition.createdAt} updatedAt={openPosition.updatedAt} />
      ),
    },
  ];

  return (
    <DetailPageLayout
      variant={CenteredLayoutVariant.Wide}
      header={
        <DetailHeader
          icon={<ReceiptEuroIcon />}
          title={openPosition.invoiceNumber || <EmptyValue />}
          subtitle={openPosition.customer ? getCustomerDisplayName(openPosition.customer) : undefined}
          badges={
            <Badge
              variant={BadgeVariant.Outlined}
              colorVariant={isPayment ? BadgeColorVariant.Primary : BadgeColorVariant.Secondary}
              className="rounded-full"
            >
              {t(isPayment ? "fia:Payment" : "fia:Receivable")}
            </Badge>
          }
          actions={detailActions}
        />
      }
    >
      <DetailTabs sections={tabs} trackingName={OPEN_POSITION_DETAIL_TRACKING_NAME} />
    </DetailPageLayout>
  );
}
